import {
  CREDIT_INVOICE,
  InvoiceSubtype,
  VatCode,
  VatFreeTextForVatCode,
} from "./constants";
import { InvoiceDisplay, InvoiceEdit, InvoiceRow } from "./invoiceAPI";
import roundHalfUp from "../../common/helpers/roundHalfUp";

export interface VatPercentCategory {
  vatRatePercent: string;
  vatAmount: number;
  vatCode: string;
  vatExcludedAmount: number;
  vatFreeText: string;
}

export const getVatFreeText = (vatCode: VatCode) => {
  return VatFreeTextForVatCode[vatCode];
};

export const isVatRatePercentZero = (vatRatePercent: string) => {
  const zeroes = ["0", "0.0", "0.00"];
  return zeroes.includes(vatRatePercent);
};

const getNumberValue = (number: string) =>
  Number.isNaN(parseFloat(number)) ? 0 : parseFloat(number);

const toHundredths = (value: number) =>
  Number.isFinite(value) ? Math.round(value * 100) : 0;

// Quantity, unit price and percent each have at most two decimals, so
// scaling them to integers keeps the product exact and avoids float
// midpoint errors (e.g. 1.5 × 4.05 = 6.075 stored as 6.07499…).
const multiplyByPercent = (
  quantity: number,
  unitPrice: number,
  percent: number,
) =>
  roundHalfUp(
    (toHundredths(quantity) * toHundredths(unitPrice) * toHundredths(percent)) /
      1e8,
  );

const calculateVatAmount = (vatExcludedAmount: number, vatPercent: number) =>
  roundHalfUp(
    (toHundredths(vatExcludedAmount) * toHundredths(vatPercent)) / 1e6,
  );

const addAmounts = (a: number, b: number) =>
  (toHundredths(a) + toHundredths(b)) / 100;

export const calculateInvoiceRowAmounts = ({
  quantity,
  unitPrice,
  discountPercent,
  vatPercent,
}: {
  quantity: number;
  unitPrice: number;
  discountPercent: number;
  vatPercent: number;
}) => {
  const vatExcludedAmount = multiplyByPercent(
    quantity,
    unitPrice,
    100 - (Number.isFinite(discountPercent) ? discountPercent : 0),
  );
  const vatAmount = calculateVatAmount(vatExcludedAmount, vatPercent);
  return {
    vatExcludedAmount,
    vatAmount,
    amount: roundHalfUp(vatExcludedAmount + vatAmount),
  };
};

export const toTwoDecimalString = (value: string) => {
  // Returns non-numeric values as is instead of converting to 0, so backend rejects the request
  const number = parseFloat(value);
  return Number.isNaN(number) ? value : roundHalfUp(number).toFixed(2);
};

export const calculateInvoiceRowVats = (rows: InvoiceRow[]) => {
  let newVatSums: VatPercentCategory[] = [];
  rows?.forEach((row) => {
    const rowVatExcludedAmount = getNumberValue(row.vatExcludedAmount);
    const isZeroVatRatePercent = isVatRatePercentZero(row.vatRatePercent);
    const isExistingVatPercentAndVatCode = newVatSums.find(
      (v) =>
        parseFloat(v.vatRatePercent) === parseFloat(row.vatRatePercent) &&
        v.vatCode === row.vatCode,
    );
    const isExistingVatPercent = newVatSums.find(
      (v) => parseFloat(v.vatRatePercent) === parseFloat(row.vatRatePercent),
    );

    const getExistingVatSumObject = (vatSum: VatPercentCategory) => {
      const newVatExcludedAmount = addAmounts(
        vatSum.vatExcludedAmount,
        rowVatExcludedAmount,
      );
      const newVatAmount = calculateVatAmount(
        newVatExcludedAmount,
        parseFloat(vatSum.vatRatePercent),
      );
      return {
        vatRatePercent: vatSum.vatRatePercent,
        vatAmount: newVatAmount,
        vatCode: vatSum.vatCode,
        vatExcludedAmount: newVatExcludedAmount,
        vatFreeText: vatSum.vatFreeText,
      };
    };

    const newVatSumObject = {
      vatRatePercent: row.vatRatePercent,
      vatAmount: calculateVatAmount(
        rowVatExcludedAmount,
        parseFloat(row.vatRatePercent),
      ),
      vatCode: row.vatCode,
      vatExcludedAmount: rowVatExcludedAmount,
      vatFreeText: getVatFreeText(row.vatCode as VatCode),
    };

    /*
      VAT summarizing:
          - Based on vatRatePercent
          - If vatRatePercent is 0
            - Based on vatCode
    */

    if (isZeroVatRatePercent) {
      if (isExistingVatPercentAndVatCode) {
        // existing zero vat item with the same vat code
        newVatSums = newVatSums.map((vatSum) => {
          return parseFloat(vatSum.vatRatePercent) ===
            parseFloat(row.vatRatePercent) && vatSum.vatCode === row.vatCode
            ? getExistingVatSumObject(vatSum)
            : vatSum;
        });
      } else {
        // new item
        newVatSums.push(newVatSumObject);
      }
    } else {
      if (isExistingVatPercent) {
        newVatSums = newVatSums.map((vatSum) => {
          return parseFloat(vatSum.vatRatePercent) ===
            parseFloat(row.vatRatePercent)
            ? getExistingVatSumObject(vatSum)
            : vatSum;
        });
      } else {
        // new item
        newVatSums.push(newVatSumObject);
      }
    }
  });

  return newVatSums;
};

export const calculateInvoiceTotals = (rows: InvoiceRow[]) => {
  const vatSums = calculateInvoiceRowVats(rows);
  const vatExcludedSum = vatSums.reduce(
    (acc, curr) => addAmounts(acc, curr.vatExcludedAmount),
    0,
  );
  const totalSum = vatSums.reduce(
    (acc, curr) => addAmounts(acc, curr.vatAmount),
    vatExcludedSum,
  );

  return { vatExcludedSum, vatSums, totalSum };
};

// Check if invoice is a credit invoice or a new repair invoice
export const isInvoicePartOfCreditInvoiceBundle = (
  invoice: InvoiceEdit | InvoiceDisplay,
) => {
  return invoice.originalInvoiceNumber !== null;
};

// Check if invoices are part of the same credit invoice bundle
export const areInvoicesCreditInvoiceBundle = (invoices: InvoiceEdit[]) => {
  if (invoices.length === 0) return false;

  // Check if all invoices array items have the same originalInvoiceNumber and bundleId
  const allInvoicesArePartOfSameCreditInvoiceBundle = invoices.every(
    (invoice) =>
      invoice.originalInvoiceNumber !== null &&
      invoice.bundleId !== null &&
      invoice.originalInvoiceNumber === invoices[0].originalInvoiceNumber &&
      invoice.bundleId === invoices[0].bundleId,
  );

  return allInvoicesArePartOfSameCreditInvoiceBundle;
};

export const isNormalSingleInvoice = (invoices: InvoiceEdit[]) => {
  if (
    invoices.length === 1 &&
    !isInvoicePartOfCreditInvoiceBundle(invoices[0])
  ) {
    return true;
  }
  return false;
};

// Determine if invoice is credit invoice based on vatExcludedAmount and vatAmount
export const isInvoiceCreditInvoice = (
  invoice: InvoiceEdit | InvoiceDisplay,
) => {
  const vatsAreNegative =
    parseFloat(invoice.vatExcludedAmount || "0") +
      parseFloat(invoice.vatAmount || "0") <
    0;

  if (isInvoicePartOfCreditInvoiceBundle(invoice) && vatsAreNegative) {
    return true;
  }

  return false;
};

// Check if we should use Credit invoice origin code or original
export const getInvoiceOriginCode = (invoice: InvoiceEdit | InvoiceDisplay) =>
  isInvoiceCreditInvoice(invoice)
    ? CREDIT_INVOICE
    : (invoice.originCode as string);

// Determine invoice type title based on invoice origin code
export const getInvoiceSubtype = (
  invoice: InvoiceEdit | InvoiceDisplay,
): InvoiceSubtype => {
  if (!isInvoicePartOfCreditInvoiceBundle(invoice))
    return InvoiceSubtype.SalesInvoice;
  if (isInvoiceCreditInvoice(invoice)) {
    return InvoiceSubtype.CreditInvoice;
  } else {
    return InvoiceSubtype.NewRepairInvoice;
  }
};
